import { useCallback, useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'mh-html-editor-preview-pct';
const DEFAULT_PCT = 44;
const MIN_PCT = 22;
const MAX_PCT = 78;

function readStoredPct(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PCT;
    const n = Number(raw);
    if (!Number.isFinite(n)) return DEFAULT_PCT;
    return Math.min(MAX_PCT, Math.max(MIN_PCT, n));
  } catch {
    return DEFAULT_PCT;
  }
}

function useMediaMinWidth(minPx: number): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(`(min-width: ${minPx}px)`).matches : false,
  );
  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${minPx}px)`);
    const onChange = () => setMatches(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [minPx]);
  return matches;
}

export function useHtmlEditorSplit(active: boolean) {
  const wide = useMediaMinWidth(720);
  const resizable = active && wide;
  const layoutRef = useRef<HTMLDivElement>(null);
  const [previewPct, setPreviewPct] = useState(readStoredPct);
  const dragging = useRef(false);

  const persistPct = useCallback((pct: number) => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Math.round(pct)));
    } catch {
      /* ignore */
    }
  }, []);

  const onPointerMove = useCallback((e: PointerEvent) => {
    if (!dragging.current || !layoutRef.current) return;
    const rect = layoutRef.current.getBoundingClientRect();
    const resizer = 10;
    const usable = rect.width - resizer;
    if (usable <= 0) return;
    const fromLeft = e.clientX - rect.left;
    const previewPx = rect.width - fromLeft - resizer / 2;
    const pct = (previewPx / usable) * 100;
    setPreviewPct(Math.min(MAX_PCT, Math.max(MIN_PCT, pct)));
  }, []);

  const endDrag = useCallback(() => {
    if (!dragging.current) return;
    dragging.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', endDrag);
    setPreviewPct((p) => {
      persistPct(p);
      return p;
    });
  }, [onPointerMove, persistPct]);

  useEffect(() => () => endDrag(), [endDrag]);

  function startDrag(e: React.PointerEvent) {
    if (!resizable || e.button !== 0) return;
    dragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    e.currentTarget.setPointerCapture(e.pointerId);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', endDrag);
  }

  function resetSplit() {
    setPreviewPct(DEFAULT_PCT);
    persistPct(DEFAULT_PCT);
  }

  const gridStyle = resizable
    ? {
        gridTemplateColumns: `minmax(0, ${100 - previewPct}fr) 10px minmax(0, ${previewPct}fr)`,
      }
    : undefined;

  return { layoutRef, resizable, previewPct, gridStyle, startDrag, resetSplit };
}
